// @ts-check
// Challenges, the app side: wording and geometry for the challenge card. The
// rules (progress, pace, the next-step line) live on the API in
// lib/challenges.js.

/**
 * @typedef {{ id: string|number, kind: "khronos"|"mate", title: string,
 *   description?: string, status: string, phase: string, starts?: string|null,
 *   ends?: string|null, daysLeft?: number, joined?: number|null,
 *   goal?: { type: string, target: number, unit: string, machine?: string },
 *   progress?: { current: number, target: number, thisWeek?: number },
 *   pace?: number|null, ahead?: boolean|null, nextStep?: string|null,
 *   badge?: { key: string, title: string, icon: string },
 *   weeks?: number, weeksMet?: number, myStatus?: string|null, role?: string,
 *   mate?: { id: number, name: string, photo_url: string|null }|null,
 *   history?: { weekStart: string, me: { days: number, target: number|null },
 *     them: { days: number, target: number|null }|null, met: boolean, finished: boolean }[] }} Challenge
 */

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "Sun 1 Nov" from "2026-11-01", read as a date, not a moment. */
export const dayLabel = (/** @type {string} */ day) => {
  const d = new Date(`${day}T12:00:00Z`);
  return `${["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

/** "November challenge", "4-week challenge". */
export const eyebrow = (/** @type {Challenge} */ c) => {
  if (c.kind === "mate") return `With ${firstName(c.mate?.name)} · ${c.weeks} weeks`;
  if (c.starts && c.ends && c.starts.slice(0, 7) === c.ends.slice(0, 7)) {
    return `${MONTHS_LONG[Number(c.starts.slice(5, 7)) - 1]} challenge`;
  }
  return "Challenge";
};

export const firstName = (/** @type {string|undefined|null} */ name) => (name || "").trim().split(/\s+/)[0] || "Your mate";

/** A goal amount in its unit: "27.4", "7". */
export const amount = (/** @type {number} */ n, /** @type {string|undefined} */ unit) =>
  unit === "km" ? n.toLocaleString("en-AU", { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : String(n);

/** "of 42.2 km", "of 12 sessions", "of 3 days". */
export const ofTarget = (/** @type {Challenge} */ c) => {
  const unit = c.goal?.unit || "";
  const target = c.progress?.target ?? c.goal?.target ?? 0;
  return `of ${amount(target, unit)} ${unit}`.trim();
};

/** How full the bar is, and where the pace marker sits, 0–100. */
export const barGeometry = (/** @type {Challenge} */ c) => {
  if (c.kind === "mate") {
    const total = c.weeks || 1;
    return { fill: Math.min(100, ((c.weeksMet || 0) / total) * 100), pace: null };
  }
  const target = c.progress?.target || c.goal?.target || 1;
  const fill = Math.min(100, ((c.progress?.current || 0) / target) * 100);
  const pace = c.pace == null || c.phase !== "running" ? null : Math.min(100, (c.pace / target) * 100);
  return { fill, pace };
};

/**
 * The pill: where you stand, in two words. Tone picks the colour.
 * @returns {{ label: string, tone: "ok"|"warn"|"dim" } | null}
 */
export const pill = (/** @type {Challenge} */ c) => {
  if (c.status === "completed" || c.status === "passed") return { label: "Done", tone: "ok" };
  if (c.status === "missed") return { label: "Missed a week", tone: "dim" };
  if (c.kind === "mate") {
    if (c.status === "pending") return { label: c.myStatus === "invited" ? "Invite" : "Waiting", tone: "dim" };
    if (c.phase === "upcoming") return { label: `Starts ${dayLabel(c.starts || "")}`, tone: "dim" };
    return { label: `${c.weeksMet || 0} of ${c.weeks} weeks`, tone: "ok" };
  }
  if (c.phase === "upcoming") return { label: `Starts ${dayLabel(c.starts || "")}`, tone: "dim" };
  if (c.phase === "ended") return { label: "Ended", tone: "dim" };
  if (c.ahead == null) return null;
  return c.ahead ? { label: "Ahead of pace", tone: "ok" } : { label: "Behind", tone: "warn" };
};

/** "12 days left", "Last day", "Ended". */
export const daysLeftLabel = (/** @type {Challenge} */ c) => {
  if (c.phase === "ended") return "Ended";
  if (c.kind === "mate" && c.phase === "running") return `Week ${Math.max(1, c.history?.length || 1)} of ${c.weeks}`;
  if (c.phase === "upcoming") return `Starts ${dayLabel(c.starts || "")}`;
  if (!c.daysLeft) return "Last day";
  return `${c.daysLeft} ${c.daysLeft === 1 ? "day" : "days"} left`;
};

/** Challenges you're in (or invited to) go on the card; the rest wait in the picker. */
export const splitChallenges = (/** @type {{ challenges?: Challenge[], mates?: Challenge[] } | null} */ data) => {
  const khronos = data?.challenges || [];
  const mates = data?.mates || [];
  return {
    mine: [...mates.filter((m) => m.status !== "pending" || m.myStatus !== "invited"), ...khronos.filter((c) => c.status !== "open")],
    invites: mates.filter((m) => m.status === "pending" && m.myStatus === "invited"),
    open: khronos.filter((c) => c.status === "open" && c.phase !== "ended"),
  };
};
